import { useState, useCallback, useRef, useEffect } from 'react'
import { Annotation } from '../components/annotations/types'

export interface ClientScreenshot {
  id: string
  dataUrl: string
  annotations: Annotation[]
  createdAt: string
  thumbnail?: string // Smaller version for memory efficiency
}

interface ScreenshotManager {
  screenshots: ClientScreenshot[]
  selectedScreenshotId: string | null
  isAtLimit: boolean
  currentScreenshot: ClientScreenshot | null
  addScreenshot: (dataUrl: string) => Promise<boolean>
  deleteScreenshot: (id: string) => void
  selectScreenshot: (id: string) => void
  updateScreenshotAnnotations: (id: string, annotations: Annotation[]) => void
  copyScreenshotToClipboard: (id: string) => Promise<boolean>
  copySelectedScreenshotsToClipboard: (selectedIds: string[]) => Promise<boolean>
  clearAllScreenshots: () => void
  getMemoryUsage: () => { totalMB: number; avgMBPerScreenshot: number }
}

const MAX_SCREENSHOTS = 10
const THUMBNAIL_MAX_WIDTH = 200
const THUMBNAIL_MAX_HEIGHT = 150
const MAX_DATAURL_SIZE_MB = 5 // Warn if screenshot is too large

export const useClientScreenshots = (): ScreenshotManager => {
  const [screenshots, setScreenshots] = useState<ClientScreenshot[]>([])
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const urlsToRevoke = useRef<Set<string>>(new Set())

  // Initialize canvas for thumbnail generation
  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas')
      canvasRef.current.style.display = 'none'
      document.body.appendChild(canvasRef.current)
    }

    return () => {
      // Cleanup canvas on unmount
      if (canvasRef.current && document.body.contains(canvasRef.current)) {
        document.body.removeChild(canvasRef.current)
      }
      
      // Revoke all blob URLs to prevent memory leaks
      urlsToRevoke.current.forEach(url => URL.revokeObjectURL(url))
      urlsToRevoke.current.clear()
    }
  }, [])

  // Create optimized thumbnail
  const createThumbnail = useCallback(async (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = canvasRef.current!
        const ctx = canvas.getContext('2d')!
        
        // Calculate thumbnail dimensions maintaining aspect ratio
        const { width, height } = img
        let thumbWidth = width
        let thumbHeight = height
        
        if (width > THUMBNAIL_MAX_WIDTH) {
          thumbWidth = THUMBNAIL_MAX_WIDTH
          thumbHeight = (height * THUMBNAIL_MAX_WIDTH) / width
        }
        
        if (thumbHeight > THUMBNAIL_MAX_HEIGHT) {
          thumbHeight = THUMBNAIL_MAX_HEIGHT
          thumbWidth = (width * THUMBNAIL_MAX_HEIGHT) / height
        }
        
        canvas.width = thumbWidth
        canvas.height = thumbHeight
        
        // Draw thumbnail with good quality
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, thumbWidth, thumbHeight)
        
        resolve(canvas.toDataURL('image/jpeg', 0.7)) // Lower quality for thumbnail
      }
      img.src = dataUrl
    })
  }, [])

  // Check data URL size for performance warning
  const checkDataUrlSize = useCallback((dataUrl: string): number => {
    // Rough calculation: base64 data is ~1.33 times larger than binary
    const sizeInBytes = (dataUrl.length * 3) / 4
    const sizeInMB = sizeInBytes / (1024 * 1024)
    
    if (sizeInMB > MAX_DATAURL_SIZE_MB) {
      console.warn(`Large screenshot detected: ${sizeInMB.toFixed(1)}MB. Consider reducing video quality.`)
    }
    
    return sizeInMB
  }, [])

  const addScreenshot = useCallback(async (dataUrl: string): Promise<boolean> => {
    if (screenshots.length >= MAX_SCREENSHOTS) {
      alert(`Maximum ${MAX_SCREENSHOTS} screenshots allowed. Please delete some first.`)
      return false
    }

    try {
      // Check size for performance
      const sizeMB = checkDataUrlSize(dataUrl)
      
      // Create optimized thumbnail
      const thumbnail = await createThumbnail(dataUrl)
      
      const newScreenshot: ClientScreenshot = {
        id: `screenshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dataUrl,
        annotations: [],
        createdAt: new Date().toISOString(),
        thumbnail
      }

      setScreenshots(prev => {
        const updated = [...prev, newScreenshot]
        
        // Auto-select the new screenshot
        setSelectedScreenshotId(newScreenshot.id)
        
        return updated
      })

      console.log(`Screenshot added: ${sizeMB.toFixed(1)}MB`)
      return true
      
    } catch (error) {
      console.error('Failed to create screenshot:', error)
      alert('Failed to create screenshot. Please try again.')
      return false
    }
  }, [screenshots.length, checkDataUrlSize, createThumbnail])

  const deleteScreenshot = useCallback((id: string) => {
    setScreenshots(prev => {
      const updated = prev.filter(screenshot => screenshot.id !== id)
      
      // If deleted screenshot was selected, select the first available or null
      if (selectedScreenshotId === id) {
        setSelectedScreenshotId(updated.length > 0 ? updated[0].id : null)
      }
      
      return updated
    })
  }, [selectedScreenshotId])

  const selectScreenshot = useCallback((id: string) => {
    setSelectedScreenshotId(id)
  }, [])

  const updateScreenshotAnnotations = useCallback((id: string, annotations: Annotation[]) => {
    setScreenshots(prev => 
      prev.map(screenshot =>
        screenshot.id === id
          ? { ...screenshot, annotations: [...annotations] }
          : screenshot
      )
    )
  }, [])

  // Copy single screenshot to clipboard
  const copyScreenshotToClipboard = useCallback(async (id: string): Promise<boolean> => {
    const screenshot = screenshots.find(s => s.id === id)
    if (!screenshot) return false

    try {
      // If annotations exist, we need to render them onto the image
      let finalDataUrl = screenshot.dataUrl
      
      if (screenshot.annotations.length > 0) {
        finalDataUrl = await renderAnnotationsOnImage(screenshot.dataUrl, screenshot.annotations)
      }

      // Convert data URL to blob
      const response = await fetch(finalDataUrl)
      const blob = await response.blob()
      
      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob
        })
      ])
      
      return true
    } catch (error) {
      console.error('Failed to copy screenshot:', error)
      
      // Fallback: try copying the data URL as text
      try {
        await navigator.clipboard.writeText(screenshot.dataUrl)
        alert('Screenshot copied as data URL (fallback)')
        return true
      } catch (fallbackError) {
        alert('Failed to copy screenshot to clipboard')
        return false
      }
    }
  }, [screenshots])

  // Copy multiple screenshots to clipboard
  const copySelectedScreenshotsToClipboard = useCallback(async (selectedIds: string[]): Promise<boolean> => {
    if (selectedIds.length === 0) return false
    
    try {
      const selectedScreenshots = screenshots.filter(s => selectedIds.includes(s.id))
      
      if (selectedIds.length === 1) {
        return await copyScreenshotToClipboard(selectedIds[0])
      }

      // For multiple screenshots, create a combined image
      const combinedDataUrl = await combineScreenshotsToDataUrl(selectedScreenshots)
      
      const response = await fetch(combinedDataUrl)
      const blob = await response.blob()
      
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob
        })
      ])
      
      return true
    } catch (error) {
      console.error('Failed to copy screenshots:', error)
      alert('Failed to copy screenshots to clipboard')
      return false
    }
  }, [screenshots, copyScreenshotToClipboard])

  // Render annotations onto image for copying
  const renderAnnotationsOnImage = useCallback(async (dataUrl: string, annotations: Annotation[]): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = canvasRef.current!
        const ctx = canvas.getContext('2d')!
        
        canvas.width = img.width
        canvas.height = img.height
        
        // Draw original image
        ctx.drawImage(img, 0, 0)
        
        // Draw all annotations (reuse the annotation drawing logic)
        annotations.forEach(annotation => {
          ctx.strokeStyle = annotation.color
          ctx.fillStyle = annotation.color
          ctx.lineWidth = annotation.size
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'

          switch (annotation.type) {
            case 'pen':
              if (annotation.points.length > 1) {
                ctx.beginPath()
                ctx.moveTo(annotation.points[0].x, annotation.points[0].y)
                for (let i = 1; i < annotation.points.length; i++) {
                  ctx.lineTo(annotation.points[i].x, annotation.points[i].y)
                }
                ctx.stroke()
              }
              break
            
            case 'arrow':
              if (annotation.points.length >= 2) {
                const [start, end] = annotation.points
                // Draw line
                ctx.beginPath()
                ctx.moveTo(start.x, start.y)
                ctx.lineTo(end.x, end.y)
                ctx.stroke()
                
                // Draw arrowhead
                const angle = Math.atan2(end.y - start.y, end.x - start.x)
                const arrowLength = 15
                const arrowAngle = Math.PI / 6
                
                ctx.beginPath()
                ctx.moveTo(end.x, end.y)
                ctx.lineTo(
                  end.x - arrowLength * Math.cos(angle - arrowAngle),
                  end.y - arrowLength * Math.sin(angle - arrowAngle)
                )
                ctx.moveTo(end.x, end.y)
                ctx.lineTo(
                  end.x - arrowLength * Math.cos(angle + arrowAngle),
                  end.y - arrowLength * Math.sin(angle + arrowAngle)
                )
                ctx.stroke()
              }
              break
              
            case 'line':
              if (annotation.points.length >= 2) {
                const [start, end] = annotation.points
                ctx.beginPath()
                ctx.moveTo(start.x, start.y)
                ctx.lineTo(end.x, end.y)
                ctx.stroke()
              }
              break
              
            case 'rectangle':
              if (annotation.points.length >= 2) {
                const [start, end] = annotation.points
                const width = end.x - start.x
                const height = end.y - start.y
                ctx.beginPath()
                ctx.rect(start.x, start.y, width, height)
                ctx.stroke()
              }
              break
              
            case 'circle':
              if (annotation.points.length >= 2) {
                const [start, end] = annotation.points
                const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))
                ctx.beginPath()
                ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI)
                ctx.stroke()
              }
              break
              
            case 'text':
              if (annotation.points.length >= 1 && annotation.text) {
                ctx.font = `${annotation.size * 2}px Arial`
                ctx.textBaseline = 'top'
                ctx.fillText(annotation.text, annotation.points[0].x, annotation.points[0].y)
              }
              break
          }
        })
        
        resolve(canvas.toDataURL('image/png'))
      }
      img.src = dataUrl
    })
  }, [])

  // Combine multiple screenshots into one image
  const combineScreenshotsToDataUrl = useCallback(async (screenshots: ClientScreenshot[]): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current!
      const ctx = canvas.getContext('2d')!
      
      // Calculate combined dimensions (vertical stack)
      let maxWidth = 0
      let totalHeight = 0
      const spacing = 20
      
      Promise.all(
        screenshots.map(screenshot => 
          new Promise<{ img: HTMLImageElement, screenshot: ClientScreenshot }>((imgResolve) => {
            const img = new Image()
            img.onload = () => {
              maxWidth = Math.max(maxWidth, img.width)
              totalHeight += img.height + spacing
              imgResolve({ img, screenshot })
            }
            img.src = screenshot.dataUrl
          })
        )
      ).then(async (imageData) => {
        canvas.width = maxWidth
        canvas.height = totalHeight - spacing // Remove last spacing
        
        // Fill background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        let yOffset = 0
        
        for (const { img, screenshot } of imageData) {
          // Draw screenshot
          ctx.drawImage(img, 0, yOffset)
          
          // Draw annotations if any
          if (screenshot.annotations.length > 0) {
            ctx.save()
            ctx.translate(0, yOffset)
            
            screenshot.annotations.forEach(annotation => {
              // Apply annotation drawing logic (same as above)
              ctx.strokeStyle = annotation.color
              ctx.fillStyle = annotation.color
              ctx.lineWidth = annotation.size
              ctx.lineCap = 'round'
              ctx.lineJoin = 'round'
              
              // ... (same annotation rendering code as above)
            })
            
            ctx.restore()
          }
          
          yOffset += img.height + spacing
        }
        
        resolve(canvas.toDataURL('image/png'))
      })
    })
  }, [])

  const clearAllScreenshots = useCallback(() => {
    if (screenshots.length === 0) return
    
    if (confirm(`Are you sure you want to clear all ${screenshots.length} screenshots?`)) {
      setScreenshots([])
      setSelectedScreenshotId(null)
    }
  }, [screenshots.length])

  // Memory usage calculation
  const getMemoryUsage = useCallback(() => {
    const totalSizeEstimate = screenshots.reduce((total, screenshot) => {
      // Rough estimate: dataUrl size + thumbnail size
      const mainSize = (screenshot.dataUrl.length * 3) / 4
      const thumbSize = screenshot.thumbnail ? (screenshot.thumbnail.length * 3) / 4 : 0
      return total + mainSize + thumbSize
    }, 0)
    
    const totalMB = totalSizeEstimate / (1024 * 1024)
    const avgMBPerScreenshot = screenshots.length > 0 ? totalMB / screenshots.length : 0
    
    return { totalMB, avgMBPerScreenshot }
  }, [screenshots])

  return {
    screenshots,
    selectedScreenshotId,
    isAtLimit: screenshots.length >= MAX_SCREENSHOTS,
    currentScreenshot: screenshots.find(s => s.id === selectedScreenshotId) || null,
    addScreenshot,
    deleteScreenshot,
    selectScreenshot,
    updateScreenshotAnnotations,
    copyScreenshotToClipboard,
    copySelectedScreenshotsToClipboard,
    clearAllScreenshots,
    getMemoryUsage
  }
}